/*
  <category-select> — an accessible replacement for the native <select>
  used by the search form's category filter.

  Why this exists: a native select is drawn by the operating system, so its
  height, row padding, corner radius and scrollbar can't be styled. With a
  large catalogue the OS renders one very long list. This component gives a
  compact, scrollable panel instead.

  Contract with the rest of the theme: the original <select> is kept in the
  DOM (visually hidden) and remains the single source of truth. Choosing an
  option writes to it and dispatches a native `change` event, so
  predictive-search.js — which reads [data-search-category].value and
  listens for `change` — and normal form submission both keep working with
  no changes.

  Accessibility: implements the ARIA combobox/listbox pattern with roving
  focus, Up/Down/Home/End, Enter/Space to commit, Escape to cancel, and
  type-ahead. The trigger reflects the current value and is focusable, so
  keyboard and screen-reader users get an equivalent experience.
*/

const TYPEAHEAD_RESET_MS = 700;

class CategorySelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.trigger = this.querySelector('[data-category-trigger]');
    this.panel = this.querySelector('[data-category-panel]');
    this.label = this.querySelector('[data-category-label]');

    if (!this.select || !this.trigger || !this.panel || !this.label) return;

    this.options = [];
    this.activeIndex = -1;
    this.typeahead = '';
    this.typeaheadTimer = null;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
    this.onPanelKeydown = this.onPanelKeydown.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);

    this.buildOptions();
    this.syncLabel();

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeydown);
    this.panel.addEventListener('keydown', this.onPanelKeydown);

    // Keep in sync if anything else changes the select programmatically.
    this.select.addEventListener('change', this.onSelectChange);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.trigger?.removeEventListener('keydown', this.onTriggerKeydown);
    this.panel?.removeEventListener('keydown', this.onPanelKeydown);
    this.select?.removeEventListener('change', this.onSelectChange);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    clearTimeout(this.typeaheadTimer);
  }

  /* ---------- building ---------- */

  buildOptions() {
    this.panel.innerHTML = '';
    this.options = Array.from(this.select.options).map((option, index) => {
      const item = document.createElement('li');
      item.id = `${this.select.id}-option-${index}`;
      item.className = 'category-select__option';
      item.setAttribute('role', 'option');
      item.setAttribute('data-value', option.value);
      item.textContent = option.textContent.trim();
      item.setAttribute('aria-selected', option.selected ? 'true' : 'false');

      // pointerdown rather than click: the panel closes on document
      // pointerdown, and click would fire after that teardown.
      item.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.commit(index);
      });
      item.addEventListener('pointerenter', () => this.setActive(index, false));

      this.panel.appendChild(item);
      return item;
    });
  }

  /* ---------- state ---------- */

  get isOpen() {
    return this.hasAttribute('open');
  }

  syncLabel() {
    const selected = this.select.options[this.select.selectedIndex];
    this.label.textContent = selected ? selected.textContent.trim() : '';
    this.options.forEach((item, index) => {
      item.setAttribute('aria-selected', index === this.select.selectedIndex ? 'true' : 'false');
    });
  }

  onSelectChange() {
    this.syncLabel();
  }

  /* ---------- open / close ---------- */

  open() {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    this.setActive(this.select.selectedIndex >= 0 ? this.select.selectedIndex : 0, true);
    this.panel.focus();
  }

  close({ focusTrigger = true } = {}) {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.removeAttribute('aria-activedescendant');
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    this.options.forEach((item) => item.classList.remove('is-active'));
    this.activeIndex = -1;
    if (focusTrigger) this.trigger.focus();
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  onDocumentPointerDown(event) {
    if (!this.contains(event.target)) this.close({ focusTrigger: false });
  }

  /* ---------- active option ---------- */

  setActive(index, scroll) {
    if (index < 0 || index >= this.options.length) return;
    this.options.forEach((item) => item.classList.remove('is-active'));
    const item = this.options[index];
    item.classList.add('is-active');
    this.activeIndex = index;
    this.panel.setAttribute('aria-activedescendant', item.id);
    if (scroll) item.scrollIntoView({ block: 'nearest' });
  }

  move(delta) {
    const next = Math.min(Math.max(this.activeIndex + delta, 0), this.options.length - 1);
    this.setActive(next, true);
  }

  commit(index) {
    if (index < 0 || index >= this.options.length) return;
    this.select.selectedIndex = index;
    this.syncLabel();
    // Native event so predictive-search.js and any form logic react as if
    // the user had used the original select.
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  }

  /* ---------- keyboard ---------- */

  onTriggerClick(event) {
    event.preventDefault();
    this.toggle();
  }

  onTriggerKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.open();
        break;
      default:
        break;
    }
  }

  onPanelKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0, true);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.options.length - 1, true);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.commit(this.activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close({ focusTrigger: false });
        break;
      default:
        if (event.key.length === 1) this.typeAhead(event.key);
        break;
    }
  }

  typeAhead(char) {
    clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    this.typeaheadTimer = setTimeout(() => {
      this.typeahead = '';
    }, TYPEAHEAD_RESET_MS);

    const match = this.options.findIndex((item) =>
      item.textContent.trim().toLowerCase().startsWith(this.typeahead)
    );
    if (match >= 0) this.setActive(match, true);
  }
}

customElements.define('category-select', CategorySelect);
